<script lang="ts">
  // Entry screen, Master Duel cadence: a left command column (breathing wordmark
  // → authentication terminal) with Cyber End Dragon — the GX ace, an official
  // transparent render — filling the right sky over the animated two-tone void.
  // Corner chrome (brackets, gateway readouts, version line) frames the whole
  // viewport as a game console, not a web page.
  import { onDestroy } from "svelte";
  import { fade, fly, scale } from "svelte/transition";
  import { expoOut } from "svelte/easing";
  import { LOGIN_URL, setToken } from "../auth.ts";
  import { getSocket } from "../stores/net.svelte.ts";
  import { session } from "../stores/session.svelte.ts";
  import DataWindow from "../components/ui/DataWindow.svelte";
  import NeonButton from "../components/ui/NeonButton.svelte";
  import Field from "../components/ui/Field.svelte";
  import CredentialAuth from "../components/CredentialAuth.svelte";
  import HeroDragon from "../components/HeroDragon.svelte";

  // Seed from any sign-in rejection that bounced us back here (e.g. the beta kill switch). The
  // screen remounts on the route change, so reading it once at init is enough.
  let err = $state(session.authError ?? "");
  let busy = $state(false);
  let showCode = $state(false); // reveal the manual paste-code fallback
  let code = $state("");
  let popup: Window | null = null;
  let fallback: ReturnType<typeof setTimeout> | undefined;

  // Honour reduced-motion for the one-shot entrance transitions (the looping CSS
  // animations are already frozen via the @media rule in app.css).
  const reduced =
    typeof matchMedia !== "undefined" && matchMedia("(prefers-reduced-motion: reduce)").matches;
  const d = (ms: number) => (reduced ? 0 : ms);

  // Terminal status readout, doubles as the error channel (one line, aria-live).
  const status = $derived(
    busy
      ? { tone: "busy", text: "Waiting for Discord" }
      : err
        ? { tone: "error", text: err }
        : { tone: "idle", text: "Link standby, authorise with Discord" },
  );

  // A file:// client can't ride a cookie session, so login runs in a hosted popup
  // (LOGIN_URL → Discord OAuth → token). The broker posts the token back here; we
  // store it and reconnect, which sends {t:"Auth"} on open → server replies LoginOk
  // (handled globally in App → routes home). If the popup can't talk back (blocked
  // cross-window messaging), the user pastes the code shown in the popup instead.
  function login() {
    err = "";
    session.authError = null;
    busy = true;
    popup = window.open(LOGIN_URL, "df-discord-login", "width=520,height=760");
    if (!popup) {
      busy = false;
      err = "Popup blocked, allow popups for this page and retry.";
      return;
    }
    window.addEventListener("message", onAuthMessage);
    // Reveal the paste-code fallback shortly after, in case postMessage is blocked.
    fallback = setTimeout(() => {
      showCode = true;
    }, 4000);
  }

  function onAuthMessage(e: MessageEvent) {
    const data = e.data as { source?: string; token?: unknown } | null;
    if (!data || data.source !== "ygoevo-auth" || typeof data.token !== "string") return;
    finishLogin(data.token);
  }

  function submitCode() {
    const t = code.trim();
    if (!t) {
      err = "Paste the login code from the Discord window.";
      return;
    }
    finishLogin(t);
  }

  function finishLogin(token: string) {
    window.removeEventListener("message", onAuthMessage);
    clearTimeout(fallback);
    try {
      popup?.close();
    } catch {
      /* opener may not be allowed to close a cross-origin popup */
    }
    setToken(token);
    err = "";
    busy = true;
    showCode = false;
    // Reconnect → Auth sent on open → LoginOk routes us into the world (App).
    getSocket().reconnect();
    fallback = setTimeout(() => {
      busy = false;
      if (!err) err = "Signed in, but the world didn't respond. Try again.";
    }, 8000);
  }

  onDestroy(() => {
    window.removeEventListener("message", onAuthMessage);
    clearTimeout(fallback);
  });
</script>

<!-- Four HUD corner brackets frame the whole viewport as a cyberspace console. -->
{#snippet bracket(pos: string)}
  <svg
    viewBox="0 0 44 44"
    class="pointer-events-none absolute size-9 text-cyan/35 sm:size-11 {pos}"
    fill="none"
    stroke="currentColor"
    stroke-width="1.25"
    aria-hidden="true"
  >
    <path d="M2 16 V2 H16" />
    <path d="M2 24 V2 H10" opacity="0.5" />
  </svg>
{/snippet}

<div class="tp-login relative h-full w-full overflow-hidden" style="background: var(--df-bg-menu, transparent)">
  <!-- ── Cyber End Dragon: official transparent render riding the blue zone. ── -->
  <div in:fade={{ duration: d(1000), delay: d(200) }}>
    <HeroDragon />
  </div>

  <div in:fade={{ duration: d(500), delay: d(350) }}>
    {@render bracket("left-4 top-4 sm:left-6 sm:top-6")}
    {@render bracket("right-4 top-4 rotate-90 sm:right-6 sm:top-6")}
    {@render bracket("bottom-4 right-4 rotate-180 sm:bottom-6 sm:right-6")}
    {@render bracket("bottom-4 left-4 -rotate-90 sm:bottom-6 sm:left-6")}
  </div>

  <!-- Top HUD readouts: gateway id (left) + live server status (right). -->
  <div
    class="pointer-events-none absolute inset-x-5 top-5 z-20 hidden items-center justify-between font-display text-[0.68rem] uppercase tracking-[0.26em] text-ink/70 sm:flex sm:px-8 phone:hidden"
    in:fade={{ duration: d(500), delay: d(400) }}
  >
    <span aria-hidden="true">Secure Gateway</span>
    <span class="flex items-center gap-2">
      <span class="size-1.5 rounded-full bg-lime animate-pulse-soft" style="filter:drop-shadow(0 0 5px var(--color-lime))"></span>
      Domino&nbsp;Server · Online
    </span>
  </div>

  <!-- ── Command column ── -->
  <div
    class="relative z-10 flex h-full w-full max-w-[560px] flex-col justify-center gap-[clamp(1.1rem,3.4vh,2rem)] px-[clamp(1.25rem,6vw,6.5rem)] py-14 phone:justify-start phone:gap-4 phone:overflow-y-auto phone:overflow-x-hidden phone:py-5 scroll-cyber"
  >
    <!-- Wordmark: breathing glow, left-aligned like a game title block. Semantic <h1>
         with an sr-only descriptor so crawlers/screen readers get the real product
         identity (the game name, not the decorative "Secure Gateway" HUD chrome). -->
    <h1
      in:scale={{ start: 0.94, opacity: 0, duration: d(700), delay: d(80), easing: expoOut }}
      class="brand m-0 select-none leading-[0.95]"
    >
      <span class="sr-only">YGO Evolution — the recovered Yu-Gi-Oh! Online 2: Duel Evolution MMORPG, free on web and mobile.</span>
      <div class="font-display text-[clamp(2.4rem,4.8vw,4rem)] font-bold uppercase tracking-[0.08em] text-ink">
        YGO&nbsp;<span class="tp-login-title text-cyan">Evolution</span>
      </div>
      <div class="mt-2 flex items-center gap-3">
        <!-- Magenta counter-accent — the page's one warm note against the cyan wordmark. -->
        <span class="font-display text-[clamp(0.6rem,1.2vw,0.85rem)] uppercase tracking-[0.58em] text-magenta/90">Online</span>
        <span class="relative h-px flex-1 overflow-visible bg-gradient-to-r from-magenta/50 via-cyan/40 to-transparent" aria-hidden="true">
          <span
            class="absolute -top-[2.5px] left-1/3 size-1.5 rotate-45 bg-cyan animate-pulse-soft"
            style="filter:drop-shadow(0 0 6px var(--color-cyan))"
          ></span>
        </span>
      </div>
    </h1>

    <div in:fly={{ x: -22, opacity: 0, duration: d(640), delay: d(220), easing: expoOut }} class="relative">
      <!-- Ambient rotating ring bleeding out from behind the terminal. -->
      <svg
        viewBox="0 0 200 200"
        class="animate-ring pointer-events-none absolute left-1/2 top-1/2 -z-10 size-[min(620px,150%)] -translate-x-1/2 -translate-y-1/2 text-cyan/20"
        fill="none"
        stroke="currentColor"
        aria-hidden="true"
      >
        <circle cx="100" cy="100" r="96" stroke-width="0.5" stroke-dasharray="2 5" />
        <circle cx="100" cy="100" r="78" stroke-width="0.4" stroke-dasharray="16 7" />
        {#each Array(24) as _, i (i)}
          <line
            x1="100"
            y1="2"
            x2="100"
            y2={i % 6 === 0 ? 12 : 7}
            stroke-width="0.6"
            transform="rotate({i * 15} 100 100)"
          />
        {/each}
      </svg>

      <DataWindow c={18} grid={false} class="tp-login-panel">
        <!-- Terminal title bar -->
        <div
          class="tp-login-header flex items-center justify-between border-b border-cyan-dim/40 bg-surface-2/70 px-5 py-2.5"
        >
          <span class="flex items-center gap-2">
            <span
              class="size-1.5 rounded-full bg-cyan animate-pulse-soft"
              style="filter:drop-shadow(0 0 5px var(--color-cyan))"
            ></span>
            <span
              class="font-display text-[0.72rem] font-semibold uppercase tracking-[0.26em] text-cyan"
              >Authentication</span
            >
          </span>
          <span
            class="flex items-center gap-1.5 font-display text-[0.66rem] uppercase tracking-[0.2em] text-ink-dim/75"
          >
            <svg viewBox="0 0 24 24" class="size-3" fill="none" stroke="currentColor" stroke-width="2" aria-hidden="true">
              <rect x="5" y="11" width="14" height="9" rx="1.6" />
              <path d="M8 11V7.5a4 4 0 0 1 8 0V11" />
            </svg>
            Encrypted
          </span>
        </div>

        <!-- Terminal body -->
        <div class="px-6 py-6 sm:px-7">
          <NeonButton full onclick={login} disabled={busy}>
            <span class="flex items-center justify-center gap-2.5">
              <svg viewBox="0 0 24 24" class="size-5 shrink-0" fill="currentColor" aria-hidden="true">
                <path
                  d="M20.317 4.3698a19.7913 19.7913 0 0 0-4.8851-1.5152.0741.0741 0 0 0-.0785.0371c-.211.3753-.4447.8648-.6083 1.2495-1.8447-.2762-3.68-.2762-5.4868 0-.1636-.3933-.4058-.8742-.6177-1.2495a.077.077 0 0 0-.0785-.037 19.7363 19.7363 0 0 0-4.8852 1.515.0699.0699 0 0 0-.0321.0277C.5334 9.0458-.319 13.5799.0992 18.0578a.0824.0824 0 0 0 .0312.0561c2.0528 1.5076 4.0413 2.4228 5.9929 3.0294a.0777.0777 0 0 0 .0842-.0276c.4616-.6304.8731-1.2952 1.226-1.9942a.076.076 0 0 0-.0416-.1057c-.6528-.2476-1.2743-.5495-1.8722-.8923a.077.077 0 0 1-.0076-.1277c.1258-.0943.2517-.1923.3718-.2914a.0743.0743 0 0 1 .0776-.0105c3.9278 1.7933 8.18 1.7933 12.0614 0a.0739.0739 0 0 1 .0785.0095c.1202.099.246.1981.3728.2924a.077.077 0 0 1-.0066.1276 12.2986 12.2986 0 0 1-1.873.8914.0766.0766 0 0 0-.0407.1067c.3604.698.7719 1.3628 1.225 1.9932a.076.076 0 0 0 .0842.0286c1.961-.6067 3.9495-1.5219 6.0023-3.0294a.077.077 0 0 0 .0313-.0552c.5004-5.177-.8382-9.6739-3.5485-13.6604a.061.061 0 0 0-.0312-.0286zM8.02 15.3312c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9555-2.4189 2.157-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.9555 2.4189-2.1569 2.4189zm7.9748 0c-1.1825 0-2.1569-1.0857-2.1569-2.419 0-1.3332.9554-2.4189 2.1569-2.4189 1.2108 0 2.1757 1.0952 2.1568 2.419 0 1.3332-.946 2.4189-2.1568 2.4189z"
                />
              </svg>
              {busy ? "Waiting…" : "Sign in with Discord"}
            </span>
          </NeonButton>

          {#if import.meta.env.DEV}
            <!-- Dev only: skip Discord and sign in as a local dev user (the world
                 server accepts `dev` tokens when no auth Worker is configured). -->
            <button
              type="button"
              onclick={() => finishLogin("dev")}
              class="mt-3 w-full chamfer-btn bg-void-2/70 py-2 font-display text-xs font-semibold uppercase tracking-[0.16em] text-gold/80 ring-1 ring-gold/30 transition-colors hover:text-gold"
              style="--c:7px"
            >
              ⚡ Dev Login, skip Discord
            </button>
          {/if}

          <!-- "or" divider into the username/password panel (verified via the Discord bot). -->
          <div class="my-5 flex items-center gap-3" aria-hidden="true">
            <span class="h-px flex-1 bg-cyan-dim/25"></span>
            <span class="font-display text-xs uppercase tracking-[0.3em] text-ink-dim/80">or</span>
            <span class="h-px flex-1 bg-cyan-dim/25"></span>
          </div>
          <CredentialAuth onToken={finishLogin} />

          <!-- Status / error readout -->
          <div class="mt-4 flex min-h-[1.25rem] items-center gap-2 text-sm" aria-live="polite">
            <span
              class="size-1.5 shrink-0 rounded-full {status.tone === 'error'
                ? 'bg-magenta'
                : status.tone === 'busy'
                  ? 'bg-cyan animate-pulse-soft'
                  : 'bg-cyan-dim/70'}"
              style={status.tone !== "idle" ? "filter:drop-shadow(0 0 5px currentColor)" : ""}
            ></span>
            <span class={status.tone === "error" ? "text-magenta" : "text-ink-dim"}>
              {status.text}{#if status.tone === "busy"}<span class="ellipsis"></span>{/if}
            </span>
          </div>

          <!-- Manual fallback: if the Discord window can't message this page back
               (blocked cross-window messaging), the user pastes the code it shows. -->
          {#if showCode}
            <div class="mt-4 space-y-2" in:fade={{ duration: d(220) }}>
              <label
                for="login-code"
                class="flex items-center gap-2 font-display text-xs font-semibold uppercase tracking-[0.16em] text-cyan"
              >
                Login code
              </label>
              <Field
                id="login-code"
                bind:value={code}
                placeholder="Paste your login code"
                autocomplete="off"
                onkeydown={(e: KeyboardEvent) => e.key === "Enter" && submitCode()}
              />
              <NeonButton full onclick={submitCode}>Submit code</NeonButton>
            </div>
          {:else}
            <button
              type="button"
              class="mt-3 w-full font-display text-xs uppercase tracking-[0.18em] text-ink-dim/80 transition-colors hover:text-cyan"
              onclick={() => (showCode = true)}
            >
              Window didn't return? Enter a login code
            </button>
          {/if}
        </div>
      </DataWindow>
    </div>
  </div>

  <!-- Bottom corner chrome: version + project line (game console, not web page). -->
  <div
    class="pointer-events-none absolute bottom-5 left-5 z-20 hidden font-display text-[0.62rem] uppercase tracking-[0.3em] text-ink/60 sm:left-8 sm:block phone:hidden"
    in:fade={{ duration: d(500), delay: d(450) }}
    aria-hidden="true"
  >
    Ver 0.9 Beta · Recovered World Project
  </div>
</div>

<style>
  /* Wordmark glow breathes slowly — the screen feels alive at idle. */
  .brand {
    filter: drop-shadow(0 0 26px rgba(80, 200, 255, 0.4));
    animation: brand-breathe 7s ease-in-out infinite;
  }
  @keyframes brand-breathe {
    0%,
    100% {
      filter: drop-shadow(0 0 22px rgba(80, 200, 255, 0.32));
    }
    50% {
      filter: drop-shadow(0 0 40px rgba(80, 200, 255, 0.52));
    }
  }

  /* Animated "…" for the connecting state. */
  .ellipsis::after {
    content: "";
    animation: dots 1.4s steps(4, end) infinite;
  }
  @keyframes dots {
    0% {
      content: "";
    }
    25% {
      content: ".";
    }
    50% {
      content: "..";
    }
    75% {
      content: "...";
    }
  }

  @media (prefers-reduced-motion: reduce) {
    .brand,
    .ellipsis::after {
      animation: none;
    }
  }
</style>
